<template>
  <v-container class="privacy-container">
    <v-card class="main-card mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2">mdi-shield-lock</v-icon>
        Datenschutzerklärung
        <v-spacer></v-spacer>
        <v-chip color="success" size="small">Stand: 22. März 2025</v-chip>
      </v-card-title>

      <v-card-text>
        <p class="text-body-2 mb-6">
          Diese Datenschutzerklärung informiert Sie darüber, welche Daten wir
          erheben, wie wir sie verwenden und welche Rechte Sie haben.
        </p>

        <div class="status-alert mb-6">
          <v-icon color="success" size="20" class="mr-2"
            >mdi-information</v-icon
          >
          <div>
            Diese Anwendung ist ausschließlich für die interne Verwendung
            konzipiert.
          </div>
        </div>

        <v-row>
          <!-- Verantwortliche Stelle -->
          <v-col cols="12" md="6">
            <v-card class="section-card" color="success-lighten-5">
              <v-card-title class="py-2 px-4 d-flex align-center">
                <v-icon color="success" class="mr-2">mdi-account-tie</v-icon>
                Verantwortliche Stelle
              </v-card-title>

              <v-card-text class="py-2 px-4">
                <div class="d-flex mb-2">
                  <div class="text-subtitle-2 mr-2 min-width-100">Name:</div>
                  <div>{{ ownerName }}</div>
                </div>
                <div class="d-flex mb-2">
                  <div class="text-subtitle-2 mr-2 min-width-100">Adresse:</div>
                  <div>{{ address }}</div>
                </div>
                <div class="d-flex mb-2">
                  <div class="text-subtitle-2 mr-2 min-width-100">Telefon:</div>
                  <div>{{ phone }}</div>
                </div>
                <div class="d-flex">
                  <div class="text-subtitle-2 mr-2 min-width-100">E-Mail:</div>
                  <a
                    :href="emailLink"
                    v-html="obfuscatedEmail"
                    class="email-link"
                  ></a>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Art der erfassten Daten -->
          <v-col cols="12" md="6">
            <v-card class="section-card" color="success-lighten-5">
              <v-card-title class="py-2 px-4 d-flex align-center">
                <v-icon color="success" class="mr-2">mdi-database</v-icon>
                Art der erfassten Daten
              </v-card-title>

              <v-card-text class="py-2 px-4">
                <div class="d-flex align-center mb-2">
                  <v-icon color="success" size="small" class="mr-2"
                    >mdi-chart-timeline</v-icon
                  >
                  <div>
                    <strong>Nutzungsdaten:</strong> Informationen über Ihre
                    Interaktionen
                  </div>
                </div>
                <div class="d-flex align-center mb-2">
                  <v-icon color="success" size="small" class="mr-2"
                    >mdi-account</v-icon
                  >
                  <div>
                    <strong>Benutzerkonten:</strong> Registrierungsinformationen
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-icon color="success" size="small" class="mr-2"
                    >mdi-form-textbox</v-icon
                  >
                  <div><strong>Eingabedaten:</strong> Daten aus Formularen</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Zweck der Datenverarbeitung -->
        <h2 class="section-header mt-6">
          <v-icon color="primary" class="mr-2">mdi-target</v-icon>
          Zweck der Datenverarbeitung
        </h2>

        <v-row class="mt-2">
          <v-col
            cols="12"
            sm="6"
            md="3"
            v-for="(purpose, index) in purposes"
            :key="index"
          >
            <v-card class="purpose-card" hover>
              <v-card-text class="d-flex align-center">
                <v-icon :color="purpose.color" size="24" class="mr-3">{{
                  purpose.icon
                }}</v-icon>
                <div>{{ purpose.text }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Datenspeicherung -->
        <h2 class="section-header mt-6">
          <v-icon color="primary" class="mr-2">mdi-content-save</v-icon>
          Datenspeicherung
        </h2>
        <p class="section-text">
          Wir speichern Ihre personenbezogenen Daten nur so lange, wie es für
          die Zwecke, für die sie erhoben wurden, erforderlich ist oder
          gesetzliche Aufbewahrungsfristen dies vorschreiben.
        </p>

        <!-- Datensicherheit -->
        <h2 class="section-header mt-4">
          <v-icon color="primary" class="mr-2">mdi-security</v-icon>
          Datensicherheit
        </h2>
        <p class="section-text">
          Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein,
          um Ihre Daten gegen Manipulationen, Verlust, Zerstörung und gegen den
          Zugriff unberechtigter Personen zu schützen. Unsere
          Sicherheitsmaßnahmen werden entsprechend der technologischen
          Entwicklung fortlaufend verbessert.
        </p>

        <!-- Ihre Rechte -->
        <h2 class="section-header mt-4">
          <v-icon color="primary" class="mr-2">mdi-gavel</v-icon>
          Ihre Rechte
        </h2>

        <v-row class="mt-2">
          <v-col cols="12" sm="6" v-for="(right, index) in rights" :key="index">
            <div class="d-flex align-center mb-3">
              <v-icon color="success" size="small" class="mr-2"
                >mdi-check-circle</v-icon
              >
              <div>
                <strong>{{ right.title }}:</strong> {{ right.description }}
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Weitere Abschnitte -->
        <h2 class="section-header mt-6">
          <v-icon color="primary" class="mr-2">mdi-cookie</v-icon>
          Cookies und lokale Speicherung
        </h2>
        <p class="section-text">
          Wir verwenden Cookies und lokale Speichertechnologien, um Ihre
          Nutzererfahrung zu verbessern und bestimmte Funktionen
          bereitzustellen. Diese Technologien speichern Informationen auf Ihrem
          Gerät, wie etwa Ihren Anmeldestatus oder Benutzereinstellungen.
        </p>

        <h2 class="section-header mt-4">
          <v-icon color="primary" class="mr-2">mdi-update</v-icon>
          Änderungen dieser Datenschutzerklärung
        </h2>
        <p class="section-text">
          Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie
          stets den aktuellen rechtlichen Anforderungen entspricht oder um
          Änderungen unserer Leistungen zu berücksichtigen. Für Ihren erneuten
          Besuch gilt dann die jeweils aktuelle Datenschutzerklärung.
        </p>

        <h2 class="section-header mt-4">
          <v-icon color="primary" class="mr-2">mdi-scale-balance</v-icon>
          Rechtsgrundlage der Verarbeitung
        </h2>
        <p class="section-text">
          Die Verarbeitung Ihrer personenbezogenen Daten erfolgt auf Grundlage
          der Datenschutz-Grundverordnung (DSGVO) sowie weiterer nationaler
          Datenschutzgesetze. Maßgeblich sind insbesondere Art. 6 Abs. 1 lit. b)
          und f) DSGVO, soweit die Verarbeitung zur Erfüllung eines Vertrags
          oder zur Wahrung unserer berechtigten Interessen erforderlich ist.
        </p>

        <h2 class="section-header mt-4">
          <v-icon color="primary" class="mr-2">mdi-share-variant</v-icon>
          Datenübermittlung an Dritte
        </h2>
        <p class="section-text">
          Eine Weitergabe Ihrer personenbezogenen Daten an Dritte erfolgt nur,
          sofern dies gesetzlich zulässig ist oder Sie ausdrücklich eingewilligt
          haben. Eine Übermittlung in Länder außerhalb des Europäischen
          Wirtschaftsraums findet nur statt, wenn ein angemessenes
          Datenschutzniveau gewährleistet ist.
        </p>

        <h2 class="section-header mt-4">
          <v-icon color="primary" class="mr-2">mdi-message-alert</v-icon>
          Beschwerderecht bei der Aufsichtsbehörde
        </h2>
        <p class="section-text">
          Sollten Sie der Ansicht sein, dass die Verarbeitung Ihrer
          personenbezogenen Daten gegen geltendes Datenschutzrecht verstößt,
          steht Ihnen das Recht zu, sich bei einer zuständigen
          Datenschutz-Aufsichtsbehörde zu beschweren.
        </p>

        <!-- Kontakt -->
        <h2 class="section-header mt-6">
          <v-icon color="primary" class="mr-2">mdi-card-account-phone</v-icon>
          Kontakt
        </h2>
        <p class="section-text mb-4">
          Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer
          personenbezogenen Daten, bei Auskünften, Berichtigung, Sperrung oder
          Löschung von Daten wenden Sie sich bitte an:
        </p>

        <v-card class="contact-card" color="success-lighten-5">
          <v-card-text class="d-flex flex-column">
            <div class="d-flex align-center mb-3">
              <v-icon color="success" size="20" class="mr-3">mdi-email</v-icon>
              <a
                :href="emailLink"
                v-html="obfuscatedEmail"
                class="email-link"
              ></a>
            </div>
            <div class="d-flex align-center">
              <v-icon color="success" size="20" class="mr-3">mdi-phone</v-icon>
              <span>{{ phone }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { computed } from "vue";

const ownerName = computed(() => "Andreas Reschke");
const address = computed(() => "Merscheider Str. 15, 42699 Solingen");
const phone = computed(() => "+49 176 72242015");

// Definition der E-Mail-Bestandteile:
const emailUser = "datenschutz";
const emailDomain = "alphadystopie";
const emailTld = "de";

// Zusammensetzen der E-Mail-Adresse:
const email = emailUser + "@" + emailDomain + "." + emailTld;

// Erzeugung des klickbaren Mailto-Links:
const emailLink = computed(() => `mailto:${email}`);

// Funktion zur Umwandlung in HTML-Entities:
function toHtmlEntities(str) {
  return str
    .split("")
    .map((char) => "&#" + char.charCodeAt(0) + ";")
    .join("");
}

// Erzeugung der obfuskierten E-Mail-Adresse:
const obfuscatedEmail = computed(() => toHtmlEntities(email));

// Definierte Verarbeitungszwecke mit mehr grünen Akzenten
const purposes = [
  {
    text: "Benutzerauthentifizierung",
    icon: "mdi-account-check",
    color: "success",
  },
  {
    text: "Bereitstellung der Dienste",
    icon: "mdi-monitor-dashboard",
    color: "success",
  },
  { text: "Interne Statistiken", icon: "mdi-chart-bar", color: "success" },
  { text: "Sicherheit des Systems", icon: "mdi-security", color: "success" },
];

// Definierte Benutzerrechte
const rights = [
  {
    title: "Auskunftsrecht",
    description: "Informationen über gespeicherte Daten anfordern.",
  },
  {
    title: "Berichtigungsrecht",
    description: "Korrektur falscher oder unvollständiger Daten.",
  },
  {
    title: "Löschungsrecht",
    description: "Unter bestimmten Umständen Löschung Ihrer Daten verlangen.",
  },
  {
    title: "Einschränkungsrecht",
    description: "Einschränkung der Verarbeitung verlangen.",
  },
  {
    title: "Widerspruchsrecht",
    description: "Gegen die Verarbeitung Widerspruch einlegen.",
  },
  {
    title: "Datenübertragbarkeit",
    description: "Daten in maschinenlesbarem Format erhalten.",
  },
];
</script>

<style scoped>
.privacy-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

.main-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.section-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 8px;
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.min-width-100 {
  min-width: 100px;
}

.status-alert {
  display: flex;
  align-items: center;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  border-left: 3px solid rgba(76, 175, 80, 0.7);
}

.section-header {
  font-size: 1.25rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
}

.section-text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  line-height: 1.6;
}

.purpose-card {
  background: rgba(30, 30, 35, 0.4);
  border-radius: 8px;
  transition: all 0.2s ease;
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.purpose-card:hover {
  transform: translateY(-2px);
  background: rgba(30, 30, 35, 0.6);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.contact-card {
  border-radius: 10px;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.email-link {
  color: rgba(76, 175, 80, 0.9);
  text-decoration: none;
  transition: color 0.2s ease;
}

.email-link:hover {
  color: rgba(76, 175, 80, 1);
  text-decoration: underline;
}

/* Responsive Anpassungen */
@media (max-width: 600px) {
  .section-header {
    font-size: 1.1rem;
  }

  .section-text {
    font-size: 0.9rem;
  }
}
</style>
